/* @jsx h */
import { cn } from "../utils";
import { LemmonSwitcher } from "../LemmonSwitcher";
import { useLocalization } from "../Localization";
import { computed, defineComponent, h } from "vue";

export type DateViewMode = "day" | "week" | "all";

const formatDateForInput = (date: Date) => {
  const localDate = new Date(date);
  localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
  return localDate.toISOString().split("T")[0];
};

const parseInputDate = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

export type DateSelectorProps = {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  viewMode?: DateViewMode;
  compact?: boolean;
  /** Tailwind height classes (e.g. "!h-[30px] sm:!h-[40px]") overriding the default fixed height. */
  controlHeightClass?: string;
};

export const DateSelector = defineComponent<DateSelectorProps>({
  name: "DateSelector",
  props: [
    "selectedDate",
    "onDateChange",
    "viewMode",
    "compact",
    "controlHeightClass",
  ],
  setup(props) {
    const { t } = useLocalization();

    const getMonday = (date: Date) => {
      const day = date.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate() + diff,
      );
    };

    const isToday = computed(() => {
      const today = new Date();
      const selected = props.selectedDate;
      return (
        selected.getFullYear() === today.getFullYear() &&
        selected.getMonth() === today.getMonth() &&
        selected.getDate() === today.getDate()
      );
    });

    const isThisWeek = computed(() => {
      const todayMonday = getMonday(new Date());
      const selectedMonday = getMonday(props.selectedDate);
      return todayMonday.getTime() === selectedMonday.getTime();
    });

    const dayLabel = computed(() => {
      if (isToday.value) return "Today";
      const today = new Date();
      const selected = props.selectedDate;
      const diffDays = Math.round(
        (new Date(
          selected.getFullYear(),
          selected.getMonth(),
          selected.getDate(),
        ).getTime() -
          new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
          ).getTime()) /
          (24 * 60 * 60 * 1000),
      );
      if (diffDays === 1) return "Tomorrow";
      if (diffDays === -1) return "Yesterday";
      return selected.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    });

    const weekLabel = computed(() => {
      const selectedMonday = getMonday(props.selectedDate);
      const sunday = new Date(selectedMonday);
      sunday.setDate(sunday.getDate() + 6);

      if (isThisWeek.value) return "This Week";

      const todayMonday = getMonday(new Date());
      const diffWeeks = Math.round(
        (selectedMonday.getTime() - todayMonday.getTime()) /
          (7 * 24 * 60 * 60 * 1000),
      );
      if (diffWeeks === 1) return "Next Week";
      if (diffWeeks === -1) return "Last Week";

      const fmt = (date: Date) =>
        date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      return `${fmt(selectedMonday)} - ${fmt(sunday)}`;
    });

    const showReturn = computed(() =>
      props.viewMode === "week" ? !isThisWeek.value : !isToday.value,
    );

    const goToNextDay = () => {
      const step = props.viewMode === "week" ? 7 : 1;
      const next = new Date(props.selectedDate);
      next.setDate(next.getDate() + step);
      props.onDateChange(next);
    };

    const goToPreviousDay = () => {
      const step = props.viewMode === "week" ? 7 : 1;
      const prev = new Date(props.selectedDate);
      prev.setDate(prev.getDate() - step);
      props.onDateChange(prev);
    };

    const goToToday = () => props.onDateChange(new Date());

    const openNativeDatePicker = (event: MouseEvent) => {
      const input = event.currentTarget as HTMLInputElement & {
        showPicker?: () => void;
      };
      if (!input.showPicker) return;

      try {
        input.showPicker();
      } catch {
        input.focus();
      }
    };

    const handleInputChange = (event: Event) => {
      const value = (event.target as HTMLInputElement).value;
      const nextDate = parseInputDate(value);
      if (nextDate) props.onDateChange(nextDate);
    };

    return () => {
      const isCompact = Boolean(props.compact);
      const controlHeight = isCompact ? "30px" : "36px";
      // Compact stays narrow (no chevrons, just wide enough for the week-range
      // label) until the big-tablet breakpoint so it shares one row with the
      // action buttons without wrapping.
      const widthClass = isCompact ? "w-[120px] xl:w-[140px]" : "w-[180px]";
      const chevronVisibility = isCompact ? "hidden lg:flex" : "hidden sm:flex";
      const label =
        props.viewMode === "week" ? weekLabel.value : dayLabel.value;
      // When a responsive height class is supplied, drive height via classes
      // (so it can vary per breakpoint); otherwise fall back to fixed inline height.
      const heightClass = props.controlHeightClass ?? "";
      const heightStyle = props.controlHeightClass
        ? {}
        : { height: controlHeight, minHeight: controlHeight };

      return (
        <div class="flex items-center gap-0 sm:gap-1 p-0">
          <button
            type="button"
            class={cn(
              "btn btn-ghost px-1 sm:px-2",
              chevronVisibility,
              heightClass,
            )}
            style={heightStyle}
            onClick={goToPreviousDay}
            aria-label={
              props.viewMode === "week" ? "Previous week" : "Previous day"
            }
          >
            <span class="material-symbols-outlined text-lg sm:text-xl">
              chevron_left
            </span>
          </button>

          <div
            class={cn("relative flex items-center", widthClass, heightClass)}
            style={heightStyle}
          >
            <div
              class={cn(
                "input input-bordered flex w-full items-center justify-center select-none whitespace-nowrap pr-8 text-center",
                heightClass,
              )}
              style={{
                ...heightStyle,
                fontSize: isCompact ? "11px" : "13px",
              }}
              aria-hidden="true"
            >
              <span
                class={cn(isCompact ? "text-xs" : "text-sm", "font-medium")}
              >
                {label}
              </span>
            </div>
            <input
              type="date"
              value={formatDateForInput(props.selectedDate)}
              onChange={handleInputChange}
              onClick={openNativeDatePicker}
              class="absolute left-0 top-0 h-full cursor-pointer opacity-0"
              style={{
                width: showReturn.value ? "calc(100% - 2.25rem)" : "100%",
              }}
              aria-label={
                props.viewMode === "week" ? "Select week date" : "Select date"
              }
            />
            <button
              type="button"
              class={cn(
                "absolute right-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center transition-all duration-300",
                showReturn.value
                  ? "text-accent hover:text-accent/80 cursor-pointer"
                  : "text-base-content/40",
                !showReturn.value && "pointer-events-none",
              )}
              onClick={(event) => {
                event.stopPropagation();
                if (showReturn.value) goToToday();
              }}
              disabled={!showReturn.value}
              title={showReturn.value ? t("general.date.today") : undefined}
              aria-label={showReturn.value ? "Return to today" : "Current date"}
            >
              <span
                class={cn(
                  "material-symbols-outlined transition-transform duration-300",
                  isCompact ? "text-sm" : "text-lg",
                )}
                style={{
                  transform: showReturn.value
                    ? "rotate(-360deg)"
                    : "rotate(0deg)",
                }}
              >
                {showReturn.value
                  ? "undo"
                  : props.viewMode === "week"
                    ? "date_range"
                    : "calendar_today"}
              </span>
            </button>
          </div>

          <button
            type="button"
            class={cn(
              "btn btn-ghost px-1 sm:px-2",
              chevronVisibility,
              heightClass,
            )}
            style={heightStyle}
            onClick={goToNextDay}
            aria-label={props.viewMode === "week" ? "Next week" : "Next day"}
          >
            <span class="material-symbols-outlined text-lg sm:text-xl">
              chevron_right
            </span>
          </button>
        </div>
      );
    };
  },
}) as new () => {
  $props: DateSelectorProps;
};

export type DayWeekDateSelectorProps = {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  viewMode: DateViewMode;
  onViewModeChange: (mode: DateViewMode) => void;
  compact?: boolean;
  className?: string;
  /** When true, adds an "All" option (no date filter) as the last segment. */
  allowAll?: boolean;
  /** Tailwind height classes (e.g. "!h-[30px] sm:!h-[40px]") for the switcher + date selector. */
  controlHeightClass?: string;
  /** Where to render the view switcher relative to the date selector. Default "start". */
  switcherPosition?: "start" | "end";
};

export const DayWeekDateSelector = defineComponent<DayWeekDateSelectorProps>({
  name: "DayWeekDateSelector",
  props: [
    "selectedDate",
    "onDateChange",
    "viewMode",
    "onViewModeChange",
    "compact",
    "className",
    "allowAll",
    "controlHeightClass",
    "switcherPosition",
  ],
  setup(props) {
    return () => {
      // Order: Today (day) → This Week (week) → All (optional, last)
      const options = [
        {
          value: "day",
          label: "Today",
          icon: "calendar_today",
          ariaLabel: "Day view",
          mobileLabel: "Today",
        },
        {
          value: "week",
          label: "This Week",
          icon: "date_range",
          ariaLabel: "Week view",
          mobileLabel: "Week",
        },
        ...(props.allowAll
          ? [
              {
                value: "all",
                label: "All",
                icon: "all_inclusive",
                ariaLabel: "All dates",
                mobileLabel: "All",
              },
            ]
          : []),
      ];

      const switcher = (
        <LemmonSwitcher
          key="switcher"
          options={options}
          value={props.viewMode}
          onChange={(value) => props.onViewModeChange(value as DateViewMode)}
          className={props.controlHeightClass ?? "h-[30px]"}
          buttonClassName="px-3 text-[11px] font-medium tracking-wide"
          iconClassName="text-[15px]"
          labelClassName="sr-only"
          indicatorClassName="top-[2px] bottom-[2px]"
          iconOnlyOnMobile={false}
        />
      );

      const dateSelector = props.viewMode !== "all" && (
        <DateSelector
          key="date"
          selectedDate={props.selectedDate}
          onDateChange={props.onDateChange}
          viewMode={props.viewMode}
          compact={props.compact}
          controlHeightClass={props.controlHeightClass}
        />
      );

      return (
        <div
          class={cn(
            "flex items-center gap-2",
            props.compact && "gap-0.5",
            props.className,
          )}
        >
          {props.switcherPosition === "end"
            ? [dateSelector, switcher]
            : [switcher, dateSelector]}
        </div>
      );
    };
  },
}) as new () => {
  $props: DayWeekDateSelectorProps;
};
